import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import LiveCard from "@/components/feature/LiveCard";
import FilterBar from "@/components/feature/FilterBar";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { HOT_KEYWORDS, filterLives, type FilterOptions } from "@/utils/liveData";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 8;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") ?? "";
  const [inputValue, setInputValue] = useState(keyword);
  const [filters, setFilters] = useState<FilterOptions>({ status: "all", region: "all", sort: "hot" });
  const [page, setPage] = useState(1);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 蜜梨直播` : "搜索直播 - 蜜梨直播",
    description: keyword
      ? `在蜜梨直播搜索“${keyword}”，查看相关直播与主播的实时结果。`
      : "搜索蜜梨直播中的直播标题与主播名，快速找到你想看的直播间。",
    keywords: "蜜梨直播搜索,直播搜索,找直播",
  });

  const result = useMemo(() => filterLives({ ...filters, keyword }), [filters, keyword]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const submitSearch = (value: string) => {
    const key = value.trim();
    setSearchParams(key ? { keyword: key } : {});
    setInputValue(key);
    setPage(1);
  };

  const updateFilters = (next: FilterOptions) => {
    setFilters(next);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ status: "all", region: "all", sort: "hot" });
    setPage(1);
  };

  const recommendations = filterLives({ sort: "viewers" }).slice(0, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="w-full flex-1 px-4 py-6 md:px-8">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950">
            {keyword ? (
              <>
                搜索 “<span className="text-primary-600">{keyword}</span>” 的结果
              </>
            ) : (
              "搜索直播"
            )}
          </h1>

          <form
            className="mt-4 flex flex-col gap-3 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              submitSearch(inputValue);
            }}
          >
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5 sm:flex-1">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                type="text"
                placeholder="输入直播标题或主播昵称"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门搜索：</span>
            {HOT_KEYWORDS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => submitSearch(item)}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row">
          <aside className="w-full shrink-0 lg:w-64">
            <h2 className="mb-3 font-heading text-sm font-bold text-foreground-950">筛选结果</h2>
            <FilterBar value={filters} onChange={updateFilters} showSort />
          </aside>

          <div className="min-w-0 flex-1">
            <p className="mb-4 text-sm text-foreground-600">
              {keyword ? (
                <>
                  共找到 <span className="font-bold text-primary-600">{result.length}</span> 个相关直播间
                </>
              ) : (
                <>
                  为你推荐 <span className="font-bold text-primary-600">{result.length}</span> 个热门直播间
                </>
              )}
            </p>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {pageItems.map((live) => (
                    <LiveCard key={live.id} live={live} />
                  ))}
                </div>
                <Pagination current={currentPage} total={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="space-y-6">
                <EmptyState
                  title={`没有找到与“${keyword}”相关的直播`}
                  description="换个关键词试试，或者看看下面为你推荐的热门直播。"
                  actionLabel="重置筛选"
                  onAction={resetFilters}
                />
                <div>
                  <h2 className="mb-3 font-heading text-sm font-bold text-foreground-950">
                    相关推荐直播
                  </h2>
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {recommendations.map((live) => (
                      <LiveCard key={live.id} live={live} size="compact" />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-secondary-100"
          >
            <i className="ri-home-4-line text-base" />
            返回首页
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}